import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Link,
  IconButton,
  useDisclosure,
  useColorModeValue,
  Stack,
  Text,
  Container,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Button,
  Divider,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  VStack,
  Image,
} from '@chakra-ui/react';
import logoIcon from '../assets/logo/aplica-ia-icone.svg';
import { HamburgerIcon, CloseIcon, ChevronDownIcon, BellIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { FiHome, FiUpload, FiDatabase, FiMessageCircle, FiBookOpen, FiLink, FiUsers, FiZap, FiActivity, FiCreditCard, FiLogOut, FiUser, FiSettings, FiLock } from 'react-icons/fi';
import ChangePasswordModal from './ChangePasswordModal';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, notificationsApi, operatorApi, billingApi, AppNotification } from '../services/api';

const levelColor = (level: string) => (level === 'critical' ? 'red' : level === 'warning' ? 'orange' : 'blue');

// Sino de notificações: badge com não lidas + popover com as recentes.
const NotificationBell = () => {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);

  useEffect(() => {
    const tick = async () => {
      try {
        setUnread(await notificationsApi.unreadCount());
      } catch {
        /* ignora */
      }
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, []);

  const open = async () => {
    try {
      const res = await notificationsApi.list();
      setItems(res.notifications);
      setUnread(res.unread);
    } catch {
      /* ignora */
    }
  };

  const markAll = async () => {
    try {
      await notificationsApi.markAllRead();
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignora */
    }
  };

  return (
    <Popover placement="bottom-end" onOpen={open}>
      <PopoverTrigger>
        <Box position="relative" cursor="pointer">
          <IconButton aria-label="Notificações" icon={<BellIcon />} variant="ghost" size="sm" />
          {unread > 0 && (
            <Badge colorScheme="red" borderRadius="full" position="absolute" top="-1" right="-1" fontSize="xs">
              {unread}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent w="360px">
        <PopoverArrow />
        <PopoverHeader fontWeight="semibold" fontSize="sm" display="flex" justifyContent="space-between" alignItems="center">
          Notificações
          {unread > 0 && (
            <Button size="xs" variant="ghost" onClick={markAll}>
              Marcar todas como lidas
            </Button>
          )}
        </PopoverHeader>
        <PopoverBody maxH="360px" overflowY="auto">
          {items.length === 0 ? (
            <Text fontSize="sm" color="gray.400" py={2}>
              Nenhuma notificação.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {items.map((n) => (
                <Box key={n.id} p={2} borderRadius="md" bg={n.read ? 'transparent' : 'gray.50'} borderLeftWidth="3px" borderLeftColor={`${levelColor(n.level)}.400`}>
                  <Text fontSize="sm" fontWeight="medium">
                    {n.title}
                  </Text>
                  {n.body && (
                    <Text fontSize="xs" color="gray.500">
                      {n.body}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

interface NavLinkProps {
  children: React.ReactNode;
  to: string;
  icon?: React.ReactElement;
  badge?: number; // número para exibir como badge
}

const NavLink = ({ children, to, icon, badge }: NavLinkProps) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      as={RouterLink}
      to={to}
      px={2}
      py={1}
      rounded={'md'}
      color={isActive ? 'brand.600' : 'gray.600'}
      bg={isActive ? 'brand.50' : 'transparent'}
      fontWeight={isActive ? 'semibold' : 'normal'}
      _hover={{ textDecoration: 'none', bg: 'brand.100', color: 'brand.700' }}
      display="flex"
      alignItems="center"
      gap={2}
      position="relative"
    >
      {icon}
      {children}
      {badge != null && badge > 0 && (
        <Badge colorScheme="yellow" borderRadius="full" fontSize="xs" ml={1}>
          {badge}
        </Badge>
      )}
    </Link>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const pwModal = useDisclosure();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/'); // volta para a landing, não para a tela de login
  };
  const [pendingCount, setPendingCount] = useState(0);
  const [isOperator, setIsOperator] = useState(false);
  const [trial, setTrial] = useState<{ is_trial: boolean; days_left: number; expired: boolean } | null>(null);

  const isAdmin = user?.role === 'admin';

  // Operador da plataforma (você) — mostra o painel do operador.
  useEffect(() => {
    operatorApi.access().then(setIsOperator).catch(() => setIsOperator(false));
  }, []);

  // Status do trial — banner de contagem/expiração.
  useEffect(() => {
    billingApi.trial().then(setTrial).catch(() => setTrial(null));
  }, []);

  // Busca quantidade de aprovações pendentes para o badge no menu admin
  useEffect(() => {
    if (!isAdmin) return;

    const fetchCount = async () => {
      try {
        const count = await adminApi.getPendingCount();
        setPendingCount(count);
      } catch {
        // ignora erros — badge simplesmente não aparece
      }
    };

    fetchCount();
    // Atualiza a cada 60 segundos enquanto logado
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Navegação data-driven: controla acesso (all/admin/operador) e o tour.
  // 'operator' = admin geral da plataforma (você); clientes não veem.
  const navItems = [
    { to: '/inicio', label: 'Início', icon: <FiHome />, show: 'all', tip: 'Seu ponto de partida e os primeiros passos.' },
    { to: '/upload', label: 'Documentos', icon: <FiUpload />, show: 'all', tip: 'Envie contratos, PDFs, planilhas e áudios — o sistema lê tudo e deixa pesquisável.' },
    { to: '/collections', label: 'Bases', icon: <FiDatabase />, show: 'operator' },
    { to: '/chat', label: 'Assistente', icon: <FiMessageCircle />, show: 'all', tip: 'Pergunte qualquer coisa e receba respostas com a fonte citada.' },
    { to: '/integrations', label: 'Integrações', icon: <FiLink />, show: 'admin', tip: 'Conecte WhatsApp, Google Drive, SharePoint e sistemas internos para o conhecimento entrar sozinho.' },
    { to: '/automations', label: 'Automações', icon: <FiZap />, show: 'admin', tip: 'Faça a IA trabalhar sozinha: um gatilho acontece, ela processa e te entrega o resultado.' },
    { to: '/groups', label: 'Grupos', icon: <FiUsers />, show: 'admin', tip: 'Organize usuários e defina quais bases de conhecimento cada grupo pode acessar.' },
    { to: '/regulatory', label: 'Regulação', icon: <FiBookOpen />, show: 'operator' },
    { to: '/operator', label: 'Operador', icon: <FiActivity />, show: 'operator' },
    { to: '/admin', label: 'Admin', icon: <FiSettings />, show: 'admin', badge: true },
  ];
  const canShow = (s: string) => s === 'all' || (s === 'admin' && isAdmin) || (s === 'operator' && isOperator);
  const visibleItems = navItems.filter((i) => canShow(i.show));
  const tourItems = visibleItems.filter((i) => i.tip);

  // Tour de boas-vindas (1ª vez do cliente admin). -1 = inativo/concluído.
  const [tourStep, setTourStep] = useState<number>(() => (localStorage.getItem('aplica_tour_done') ? -1 : 0));
  const tourActive = isAdmin && !isOperator && tourStep >= 0 && tourStep < tourItems.length;
  const endTour = () => {
    localStorage.setItem('aplica_tour_done', '1');
    setTourStep(-1);
  };

  return (
    <>
      <Box bg={useColorModeValue('white', 'gray.900')} px={4} shadow="sm">
        <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
          <IconButton
            size={'md'}
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label={'Abrir menu'}
            display={{ md: 'none' }}
            onClick={isOpen ? onClose : onOpen}
          />

          <HStack spacing={8} alignItems={'center'}>
            <HStack
              as={RouterLink}
              to="/inicio"
              spacing={2}
              _hover={{ opacity: 0.85 }}
            >
              <Image src={logoIcon} alt="Aplica IA" boxSize="34px" borderRadius="md" />
              <Text fontSize="xl" fontWeight="bold" color="brand.600">
                Aplica IA
              </Text>
            </HStack>

            <HStack as={'nav'} spacing={4} display={{ base: 'none', md: 'flex' }}>
              {visibleItems.map((item) => {
                const link = (
                  <NavLink to={item.to} icon={item.icon} badge={item.badge ? pendingCount : undefined}>
                    {item.label}
                  </NavLink>
                );
                const tIdx = tourItems.findIndex((t) => t.to === item.to);
                const open = tourActive && tourStep === tIdx;
                if (!item.tip) return <React.Fragment key={item.to}>{link}</React.Fragment>;
                return (
                  <Popover key={item.to} isOpen={open} placement="bottom" autoFocus={false} closeOnBlur={false}>
                    <PopoverTrigger>
                      <Box>{link}</Box>
                    </PopoverTrigger>
                    <PopoverContent w="280px">
                      <PopoverArrow />
                      <PopoverBody>
                        <Text fontWeight="bold" fontSize="sm" mb={1}>{item.label}</Text>
                        <Text fontSize="sm" color="gray.600" mb={3}>{item.tip}</Text>
                        <Flex justify="space-between" align="center">
                          <Button size="xs" variant="ghost" onClick={endTour}>Pular</Button>
                          <HStack spacing={2}>
                            <Text fontSize="xs" color="gray.400">{tIdx + 1}/{tourItems.length}</Text>
                            <Button
                              size="xs"
                              colorScheme="brand"
                              onClick={() => (tIdx + 1 < tourItems.length ? setTourStep(tIdx + 1) : endTour())}
                            >
                              {tIdx + 1 < tourItems.length ? 'Próximo' : 'Concluir'}
                            </Button>
                          </HStack>
                        </Flex>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                );
              })}
            </HStack>
          </HStack>

          {/* Menu do usuário */}
          <HStack spacing={4}>
            {isAdmin && <NotificationBell />}
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                leftIcon={<Avatar size="sm" name={user?.name} />}
                rightIcon={<ChevronDownIcon />}
                size="sm"
              >
                <Text display={{ base: 'none', md: 'block' }}>
                  {user?.name || user?.email}
                </Text>
              </MenuButton>
              <MenuList>
                <MenuItem icon={<FiUser />} fontSize="sm">
                  {user?.email}
                </MenuItem>
                {isAdmin && (
                  <MenuItem icon={<FiCreditCard />} fontSize="sm" as={RouterLink} to="/subscription">
                    Assinatura
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem
                    icon={<FiSettings />}
                    fontSize="sm"
                    as={RouterLink}
                    to="/admin"
                  >
                    Painel Admin
                    {pendingCount > 0 && (
                      <Badge colorScheme="yellow" ml={2}>{pendingCount}</Badge>
                    )}
                  </MenuItem>
                )}
                <MenuItem icon={<FiLock />} fontSize="sm" onClick={pwModal.onOpen}>
                  Trocar senha
                </MenuItem>
                <Divider />
                <MenuItem
                  icon={<FiLogOut />}
                  onClick={handleLogout}
                  color="red.500"
                  fontSize="sm"
                >
                  Sair
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>

        {/* Menu mobile */}
        {isOpen && (
          <Box pb={4} display={{ md: 'none' }}>
            <Stack as={'nav'} spacing={4}>
              {visibleItems.map((item) => (
                <NavLink key={item.to} to={item.to} icon={item.icon} badge={item.badge ? pendingCount : undefined}>
                  {item.label}
                </NavLink>
              ))}
              <Divider />
              <Button
                leftIcon={<FiLogOut />}
                onClick={handleLogout}
                variant="ghost"
                color="red.500"
                size="sm"
                justifyContent="flex-start"
              >
                Sair
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      {trial?.is_trial && (
        <Box bg={trial.expired ? 'red.500' : 'blue.500'} color="white" px={4} py={2}>
          <Container maxW="7xl">
            <Flex align="center" justify="center" gap={3} fontSize="sm" flexWrap="wrap">
              <Text>
                {trial.expired
                  ? 'Seu período de teste terminou — as funções de IA estão bloqueadas.'
                  : `Período de teste: ${trial.days_left} ${trial.days_left === 1 ? 'dia restante' : 'dias restantes'}.`}
              </Text>
              <Button as={RouterLink} to="/subscription" size="xs" colorScheme="whiteAlpha">
                Assinar agora
              </Button>
            </Flex>
          </Container>
        </Box>
      )}

      <Container maxW="7xl" py={6}>
        {children}
      </Container>

      <ChangePasswordModal isOpen={pwModal.isOpen} onClose={pwModal.onClose} />
    </>
  );
};

export default Layout;
